import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { Dropdown, Input, Message, Table, TextArea } from 'components/HeroSemantic';

import HeroSelectField from 'components/Field/HeroSelectField';
import obfuscate from 'utils/strings/obfuscate';
import ValidationStatus from './ValidationStatus';

export const FIELD_TYPES = {
  inputText: 'inputText',
  dropdownList: 'dropdownList',
  status: 'status',
  textArea: 'textArea',
};

const EntityDetailsFormField = ({
  children,
  compact,
  hideEmpty,
  notes,
  noteSymbol,
  obfuscated,
  options,
  placeholderId,
  readOnly,
  required,
  richOptions,
  translationId,
  type,
  validation,
  value,
  valueFormatter,
  onBlur,
  onClick,
  onChange,
}) => {
  const { formatMessage } = useIntl();
  const placeholderText = placeholderId ?
    formatMessage({ id: placeholderId, defaultMessage: 'Select…' })
    : formatMessage({ id: 'form.placeholder.select', defaultMessage: 'Select…' });

  const handleBlur = () => {
    if (typeof onBlur === 'function') {
      onBlur();
    }
  };

  const handleClick = event => {
    if (typeof onClick === 'function') {
      onClick(event);
    }
  };

  const handleChange = (event, eventData) => {
    if (typeof onChange === 'function') {
      onChange(eventData.value);
    }
  };

  const handleRichChange = val => {
    if (typeof onChange === 'function') {
      onChange(val);
    }
  };

  const isDropdownList = type === FIELD_TYPES.dropdownList && Array.isArray(options);

  // For read-only compact mode, render a muted dash placeholder when the value is empty
  // so that rows stay visually balanced instead of having blank gaps.
  const isStatusField = type === FIELD_TYPES.status;
  const dropdownText = isDropdownList ? options?.find(opt => opt.value === value)?.text : null;
  const displayedReadOnlyValue = isDropdownList ? dropdownText : (obfuscated ? obfuscate(value) : value);
  const showEmptyPlaceholder = compact && readOnly && !isStatusField &&
    (displayedReadOnlyValue === undefined || displayedReadOnlyValue === null || displayedReadOnlyValue === '');

  const fieldValueContent = (
    <>
      <div className="log-hidden-element">
        {
          (type === FIELD_TYPES.status) && (
            // Render the verified/unverified status as a compact pill so the icon
            // and label have proper spacing and visual weight (previously the
            // icon sat directly against the label with no gap).
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-sm font-semibold"
              style={{
                background: `color-mix(in srgb, var(--owner-${value ? 'success' : 'danger'}) 14%, transparent)`,
                color: value ? 'var(--owner-success)' : 'var(--owner-danger)',
              }}
            >
              <ValidationStatus failed={!value} success={!!value} />
              {formatMessage({ id: value ? 'general.verified' : 'general.unverified' })}
            </span>
          )
        }
        {
          isDropdownList && readOnly && (
            options?.find(opt => opt.value === value)?.text
          )
        }
        {
          isDropdownList && !readOnly && (options.length > 0 || !hideEmpty) && (
            richOptions ? (
              <HeroSelectField
                options={options}
                placeholder={placeholderText}
                value={value}
                onChange={handleRichChange}
              />
            ) : (
              <Dropdown
                fluid={compact}
                options={options}
                placeholder={placeholderText}
                value={value}
                style={compact ? { width: '100%' } : { minWidth: '250px' }}
                selection
                onChange={handleChange}
              />
            )
          )
        }
        {
          !isDropdownList && readOnly && (
            obfuscated ? obfuscate(value) : (valueFormatter ? valueFormatter(value) : value)
          )
        }
        {
          (type === FIELD_TYPES.inputText) && !readOnly && (
            <div className="relative w-full">
              <Input
                defaultValue={value}
                fluid={compact}
                style={compact ? { width: '100%', paddingRight: validation ? '40px' : undefined } : { minWidth: '250px', paddingRight: validation ? '40px' : undefined }}
                onBlur={handleBlur}
                onClick={handleClick}
                onChange={handleChange}
              />
              {/* Validation status sits inside the input on the right, vertically centered. */}
              {
                validation && (
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 inline-flex">
                    <ValidationStatus
                      failed={validation.failed === true}
                      loading={validation.loading === true}
                      success={validation.success === true}
                    />
                  </span>
                )
              }
            </div>
          )
        }
        {
          (type === FIELD_TYPES.textArea) && !readOnly && (
            <TextArea
              value={value}
              onClick={handleClick}
              onChange={handleChange}
            />
          )
        }
        {/* Validation status for non-input fields (textarea, dropdown) stays below — there's no
            "inside" to anchor an icon to without affecting layout. */}
        {
          !readOnly && validation && type !== FIELD_TYPES.inputText && (
            <ValidationStatus
              failed={validation.failed === true}
              loading={validation.loading === true}
              success={validation.success === true}
            />
          )
        }
      </div>
      {
        children && <div>{children}</div>
      }
    </>
  );

  const labelContent = (
    <>
      {formatMessage({ id: translationId })}
      {required && !value && (
        <span style={{ color: 'var(--owner-danger)' }}>*</span>
      )}
      {noteSymbol && (
        <span style={{ color: 'var(--owner-danger)' }}>{noteSymbol}</span>
      )}
    </>
  );

  const notesContent = Array.isArray(notes) && notes.length > 0 ? (
    <Message
      style={{
        backgroundColor: 'var(--owner-info-bg)',
        boxShadow: '0 0 0 1px var(--owner-info-border) inset, 0 0 0 0 transparent',
        color: 'var(--owner-info-text)',
      }}
      list={notes}
    />
  ) : null;

  if (compact) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
        <div style={{ color: 'var(--owner-text-muted)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', lineHeight: '16px', textTransform: 'uppercase' }}>
          {labelContent}
        </div>
        <div style={{ color: 'var(--owner-text)', fontSize: '14px', lineHeight: '22px', minHeight: readOnly ? '22px' : '40px' }}>
          {showEmptyPlaceholder ? (
            <span style={{ color: 'var(--owner-text-muted)' }}>—</span>
          ) : fieldValueContent}
        </div>
        {notesContent && <div style={{ marginTop: '4px' }}>{notesContent}</div>}
      </div>
    );
  }

  return (
    <>
      <Table.Row>
        {/* Label Column */}
        <Table.Cell style={{ color: 'var(--owner-text)', fontWeight: 700, width: '250px' }}>
          {labelContent}
        </Table.Cell>
        {/* Value Column */}
        <Table.Cell style={{ color: 'var(--owner-text)', display: 'flex', flexDirection: 'column' }}>
          {fieldValueContent}
        </Table.Cell>
      </Table.Row>
      {/* Field notes (optional) */}
      {notesContent && (
        <Table.Cell colSpan={2}>
          {notesContent}
        </Table.Cell>
      )}
    </>
  );
};

EntityDetailsFormField.propTypes = {
  children: PropTypes.node,
  compact: PropTypes.bool,
  hideEmpty: PropTypes.bool,
  noteSymbol: PropTypes.string,
  notes: PropTypes.arrayOf(PropTypes.string),
  obfuscated: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    value: PropTypes.string,
  })),
  placeholderId: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  richOptions: PropTypes.bool,
  translationId: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.values(FIELD_TYPES)).isRequired,
  validation: PropTypes.shape({
    failed: PropTypes.bool,
    loading: PropTypes.bool,
    success: PropTypes.bool,
    validator: PropTypes.func,
  }),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  valueFormatter: PropTypes.func,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
};

export default EntityDetailsFormField;
